import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPaperPlane, faRobot, faNotesMedical, faPlus, faStickyNote, faFileMedicalAlt, faBookmark as fasBookmark, faSearch } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as farBookmark } from '@fortawesome/free-regular-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPatientById } from '../../store/clinics/clinicsSlice';
import { clinicsService, type ChatConversation, type ChatMessage } from '../../services/clinicsService';
import { demoStore, isDemoModeActive } from '../../services/demoModeService';
import ClinicsPatientNotesModal from './ClinicsPatientNotesModal';
import ClinicsPatientSummaryModal from './ClinicsPatientSummaryModal';
import ChatMessageItem from './ChatMessageItem';
import { SlidingPills, type TabOption } from '../../components/ui/SlidingPills';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { ThinkingState } from '../../components/ThinkingState';
import { useNotifications } from '../../components/ui/notifications';

const ClinicsMedialHistoryChatPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const notifications = useNotifications();
  const { selectedPatient: patient } = useAppSelector((state) => state.clinics);

  const pillOptions: TabOption[] = [
    { id: 'all', label: t('clinics.chat.allPills') },
    { id: 'saved', label: t('clinics.chat.savedPills'), icon: farBookmark },
  ];

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [totalConversations, setTotalConversations] = useState(0);

  // Search & Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [savedFilter, setSavedFilter] = useState<boolean | undefined>(undefined); // undefined = all, true = saved

  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);

  const [inputMessage, setInputMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationsContainerRef = useRef<HTMLDivElement>(null);

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  // Active Save Modal state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveTitleInput, setSaveTitleInput] = useState('');

  useEffect(() => {
    if (id) {
      dispatch(fetchPatientById(Number(id)));
    }
  }, [dispatch, id]);

  // Load conversations when filters or id change (debounced)
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (id) {
        loadConversations(1, false);
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [id, searchQuery, dateFrom, dateTo, savedFilter]);

  useEffect(() => {
    if (activeConversationId && id) {
      loadMessages(activeConversationId, 1, false);
    } else {
      setMessages([]);
      setTotalMessages(0);
      setMessagesPage(1);
    }
  }, [activeConversationId, id]);

  useEffect(() => {
    // Scroll only on initial load or streaming update (not when loading historical pages)
    if (messagesPage === 1 || isStreaming) {
      scrollToBottom();
    }
  }, [messages, isStreaming]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async (page = 1, append = false) => {
    if (!id) return;
    setConversationsLoading(true);
    try {
      const params: any = {
        page,
        per_page: 15,
        search: searchQuery.trim() || undefined,
        is_saved: savedFilter,
      };

      if (dateFrom) {
        params.date_from = `${dateFrom}T00:00:00Z`;
      }
      if (dateTo) {
        params.date_to = `${dateTo}T23:59:59Z`;
      }

      const resp = await clinicsService.getConversations(Number(id), params);
      if (append) {
        setConversations(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newConvs = resp.conversations.filter(c => !existingIds.has(c.id));
          return [...prev, ...newConvs];
        });
      } else {
        setConversations(resp.conversations);
      }
      setTotalConversations(resp.total);
      setConversationsPage(page);

      // Auto-select first conversation if not appending and none selected
      if (resp.conversations.length > 0 && !activeConversationId && !append) {
        setActiveConversationId(resp.conversations[0].id);
      }
    } catch (err) {
      console.error('Failed to load conversations', err);
    } finally {
      setConversationsLoading(false);
    }
  };

  const handleConversationsScroll = () => {
    const container = conversationsContainerRef.current;
    if (!container || conversationsLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (conversations.length < totalConversations) {
        loadConversations(conversationsPage + 1, true);
      }
    }
  };

  const handleToggleSaveConversation = async (e: React.MouseEvent, conv: ChatConversation) => {
    e.stopPropagation();
    if (!id) return;
    try {
      if (conv.is_saved) {
        const updated = await clinicsService.updateConversation(Number(id), conv.id, { is_saved: false });
        setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, is_saved: updated.is_saved } : c));
      } else {
        setActiveConversationId(conv.id);
        setSaveTitleInput(conv.title || '');
        setIsSaveModalOpen(true);
      }
    } catch (err) {
      console.error('Error toggling conversation saved status', err);
    }
  };

  const handleSaveActiveConversation = async () => {
    if (!id || !activeConversationId) return;
    const activeConv = conversations.find(c => c.id === activeConversationId);
    if (!activeConv) return;

    if (activeConv.is_saved) {
      try {
        const updated = await clinicsService.updateConversation(Number(id), activeConv.id, { is_saved: false });
        setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, is_saved: updated.is_saved } : c));
      } catch (err) {
        console.error('Error toggling conversation saved status', err);
      }
    } else {
      setSaveTitleInput(activeConv.title || '');
      setIsSaveModalOpen(true);
    }
  };

  const submitSaveConversation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !activeConversationId) return;
    try {
      const updated = await clinicsService.updateConversation(Number(id), activeConversationId, {
        is_saved: true,
        title: saveTitleInput.trim() || undefined,
      });
      setConversations(prev => prev.map(c => c.id === activeConversationId ? { ...c, is_saved: true, title: updated.title } : c));
      setIsSaveModalOpen(false);
    } catch (err) {
      console.error('Error saving conversation', err);
      alert(t('clinics.chat.errorSaving'));
    }
  };

  const loadMessages = async (convId: number, page = 1, appendTop = false) => {
    if (!id) return;
    setMessagesLoading(true);
    try {
      const resp = await clinicsService.getMessages(Number(id), convId, { page, per_page: 20 });
      if (appendTop) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = resp.messages.filter(m => !existingIds.has(m.id));
          return [...newMsgs, ...prev];
        });
      } else {
        setMessages(resp.messages);
      }
      setTotalMessages(resp.total);
      setMessagesPage(page);
    } catch (err) {
      console.error('Failed to load messages', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleNewConversation = async () => {
    if (!id) return;
    try {
      const conv = await clinicsService.createConversation(Number(id), `${t('clinics.chat.newConversationPrefix')} ${new Date().toLocaleDateString()}`);
      setConversations([conv, ...conversations]);
      setTotalConversations(prev => prev + 1);
      setActiveConversationId(conv.id);
      setMessages([]);
      setTotalMessages(0);
      setMessagesPage(1);
      notifications.success('Conversación creada', 'La nueva conversación clínica está lista.');
    } catch {
      notifications.error('No se pudo crear la conversación', t('clinics.chat.errorCreating'));
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || !id || isStreaming) return;

    let convId = activeConversationId;

    // Create conversation if none exists
    if (!convId) {
      try {
        const conv = await clinicsService.createConversation(Number(id), `${t('clinics.chat.newConversationPrefix')} ${new Date().toLocaleDateString()}`);
        setConversations([conv, ...conversations]);
        convId = conv.id;
        setActiveConversationId(convId);
      } catch {
        notifications.error('No se pudo iniciar la conversación', t('clinics.chat.errorInitializing'));
        return;
      }
    }

    const content = inputMessage.trim();
    setInputMessage('');

    // Optimistic UI update
    const userMsg: ChatMessage = {
      id: Date.now(), // temporary
      conversation_id: convId,
      role: 'user',
      content,
      created_at: new Date().toISOString()
    };

    const assistantMsgPlaceholder: ChatMessage = {
      id: Date.now() + 1, // temporary
      conversation_id: convId,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg, assistantMsgPlaceholder]);
    setIsStreaming(true);

    if (isDemoModeActive()) {
      window.setTimeout(() => {
        const answer = 'Este es un resumen clínico sintético. Para la demo, revisaría antecedentes, alergias y próximas citas antes de proponer el seguimiento.';
        setMessages(prev => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant') next[next.length - 1] = { ...next[next.length - 1], content: answer };
          return next;
        });
        demoStore.mutate(() => {});
        setIsStreaming(false);
      }, 900);
      return;
    }

    try {
      // Create EventSource for SSE streaming
      const url = clinicsService.getStreamUrl(Number(id), convId);

      const token = localStorage.getItem('jwtToken');

      // Since EventSource doesn't support custom headers (like Authorization: Bearer),
      // we must use fetch to consume the SSE stream manually.
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content, language: i18n.language })
      });

      if (!response.ok) throw new Error('Network response was not ok');
      if (!response.body) throw new Error('ReadableStream not yet supported in this browser.');

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let done = false;
      let accumulatedContent = '';

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });

          // Parse SSE format
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.replace('data: ', '');
              if (data === '[DONE]') {
                done = true;
                break;
              }
              if (data.startsWith('[METADATA] ')) {
                try {
                  const metadata = JSON.parse(data.replace('[METADATA] ', ''));
                  setMessages(prev => {
                    const newArr = [...prev];
                    if (newArr.length > 0 && newArr[newArr.length - 1].role === 'assistant') {
                      newArr[newArr.length - 1].reasoning = metadata.reasoning?.join('\n') || null;
                      newArr[newArr.length - 1].consulted_documents = metadata.consulted_documents || null;
                      newArr[newArr.length - 1].consulted_data = metadata.consulted_data || null;
                    }
                    return newArr;
                  });
                } catch (e) {
                  console.error('Error parsing metadata', e);
                }
                continue;
              }
              accumulatedContent += data;

              setMessages(prev => {
                const newArr = [...prev];
                // Update the placeholder
                if (newArr.length > 0 && newArr[newArr.length - 1].role === 'assistant') {
                  newArr[newArr.length - 1].content = accumulatedContent;
                }
                return newArr;
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Error in streaming:', err);
      setMessages(prev => {
        const newArr = [...prev];
        if (newArr.length > 0 && newArr[newArr.length - 1].role === 'assistant') {
          newArr[newArr.length - 1].content = t('clinics.chat.errorStreaming');
        }
        return newArr;
      });
    } finally {
      setIsStreaming(false);
      // Reload strictly from DB to get the real IDs
      if (convId) loadMessages(convId, 1, false);
    }
  };

  return (
    <div data-ui="clinics-medical-chat-page" className="page-theme-surface page-theme-clinics flex flex-col h-[calc(100dvh-1rem)] w-full p-8 gap-8 overflow-hidden relative">
      {/* Header */}
      <div data-ui="clinics-medial-history-chat-page-region-001" className="flex flex-wrap items-center justify-between gap-4 relative z-10">
        <div data-ui="clinics-medial-history-chat-page-region-002" className="flex items-center gap-4">
          <Link to={`/clinics/patients/${id}`}>
            <BtnTaratic color="ghost-dark" size="md" iconOnly title={t('common.back')}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </BtnTaratic>
          </Link>
          <div data-ui="clinics-medial-history-chat-page-region-003" className="flex flex-col gap-1">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FontAwesomeIcon icon={faNotesMedical} className="clinics-white-shadow-icon text-blue-400 text-2xl" />
              {t('clinics.chat.title')}
            </h1>
            <div data-ui="clinics-medial-history-chat-page-region-004" className="text-sm font-medium text-white/80">
              {t('clinics.dashboard.table.patient')}: <span className="text-white font-semibold">{patient?.full_name || '...'}</span>
            </div>
          </div>
        </div>
        <div data-ui="clinics-medial-history-chat-page-region-005" className="flex items-center gap-3">
          <BtnTaratic
            onClick={() => setIsNotesModalOpen(true)}
            color="ghost-dark"
            size="md"
            className="flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faStickyNote} className="clinics-white-shadow-icon text-yellow-400" />
            {t('clinics.chat.notes')}
          </BtnTaratic>
          <BtnTaratic
            onClick={() => setIsSummaryModalOpen(true)}
            color="ghost-dark"
            size="md"
            className="flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faFileMedicalAlt} className="clinics-white-shadow-icon text-purple-300" />
            {t('clinics.chat.assistedSummary')}
          </BtnTaratic>
        </div>
      </div>

      <div data-ui="clinics-medial-history-chat-page-region-006" className="flex flex-1 gap-6 overflow-hidden min-h-0 relative z-10">
        {/* Sidebar: Conversations */}
        <div data-ui="clinics-medial-history-chat-page-region-007" className="w-1/4 min-w-[260px] rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md flex flex-col overflow-hidden hidden md:flex">
          <div data-ui="clinics-medial-history-chat-page-region-008" className="p-4 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-sm font-bold text-white">{t('clinics.chat.conversationsTitle')}</h3>
            <BtnTaratic onClick={handleNewConversation} color="blue" size="sm" className="flex items-center gap-1.5 px-2.5 py-1 text-xs">
              <FontAwesomeIcon icon={faPlus} /> {t('clinics.chat.newButton')}
            </BtnTaratic>
          </div>

          {/* Filters Area */}
          <div data-ui="clinics-medial-history-chat-page-region-009" className="p-3 border-b border-white/10 space-y-2 bg-black/20 flex-shrink-0">
            {/* Search Input */}
            <div data-ui="clinics-medial-history-chat-page-region-010" className="relative">
              <input
                type="text"
                placeholder={t('clinics.chat.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-black/75 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
              <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-2.5 text-white/30 text-xs" />
            </div>

            {/* Date Filters */}
            <div data-ui="clinics-medial-history-chat-page-region-011" className="flex gap-2 items-center">
              <div data-ui="clinics-medial-history-chat-page-region-012" className="flex-1 relative">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="w-full bg-black/75 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white/70 focus:outline-none [&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
              <span className="text-white/30 text-xs">-</span>
              <div data-ui="clinics-medial-history-chat-page-region-013" className="flex-1 relative">
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="w-full bg-black/75 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white/70 focus:outline-none [&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
            </div>

            {/* All vs Saved Toggle */}
            <div data-ui="clinics-medial-history-chat-page-region-014" className="flex w-full">
              <SlidingPills
                options={pillOptions}
                activeId={savedFilter === true ? 'saved' : 'all'}
                onChange={(id) => setSavedFilter(id === 'saved' ? true : undefined)}
                size="xs"
                variant="dark"
                className="w-full"
              />
            </div>
          </div>

          <div data-ui="clinics-medial-history-chat-page-region-015"
            ref={conversationsContainerRef}
            onScroll={handleConversationsScroll}
            className="flex-1 overflow-y-auto gemini-scrollbar p-2 space-y-1"
          >
            {conversationsLoading && conversations.length === 0 ? (
              // Conversations Skeleton
              Array.from({ length: 4 }).map((_, i) => (
                <div data-ui="clinics-medial-history-chat-page-region-016" key={i} className="animate-pulse p-3 rounded-xl border border-white/10 space-y-2 bg-white/5">
                  <div data-ui="clinics-medial-history-chat-page-region-017" className="h-4 bg-white/25 rounded w-3/4"></div>
                  <div data-ui="clinics-medial-history-chat-page-region-018" className="flex justify-between mt-1">
                    <div data-ui="clinics-medial-history-chat-page-region-019" className="h-3 bg-white/15 rounded w-1/4"></div>
                    <div data-ui="clinics-medial-history-chat-page-region-020" className="h-3 bg-white/15 rounded w-1/5"></div>
                  </div>
                </div>
              ))
            ) : conversations.length === 0 ? (
              <div data-ui="clinics-medial-history-chat-page-region-021" className="p-4 text-center text-xs text-white/40">{t('clinics.chat.noConversations')}</div>
            ) : (
              <>
                {conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => setActiveConversationId(conv.id)}
                    className={`w-full text-left p-3 rounded-xl transition-colors text-sm relative group ${activeConversationId === conv.id
                      ? 'bg-blue-500/20 border border-blue-500/30 text-white'
                      : 'border border-transparent text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                  >
                    <div data-ui="clinics-medial-history-chat-page-region-022" className="flex justify-between items-start pr-8">
                      <div data-ui="clinics-medial-history-chat-page-region-023" className="font-medium truncate flex-1">{conv.title || `${t('clinics.chat.conversationLabel')} #${conv.id}`}</div>
                    </div>
                    <div data-ui="clinics-medial-history-chat-page-region-024" className="text-xs opacity-60 mt-1 flex justify-between">
                      <span>{new Date(conv.created_at).toLocaleDateString()}</span>
                      <span>{conv.message_count} {t('clinics.chat.messagesAbbr')}</span>
                    </div>

                    {/* Bookmark Toggle Icon */}
                    <BtnTaratic
                      data-ui="clinics-save-filter-button"
                      data-pinned={conv.is_saved ? "true" : "false"}
                      onClick={(e) => handleToggleSaveConversation(e, conv)}
                      color={conv.is_saved ? "blue" : "ghost-dark"}
                      className="clinics-pinned-action-button absolute right-2 top-2 w-7 h-7 rounded-lg flex items-center justify-center p-0 group/bookmark"
                      title={conv.is_saved ? t('clinics.chat.removeFromSaved') : t('clinics.chat.saveConversation')}
                    >
                      <FontAwesomeIcon
                        icon={conv.is_saved ? fasBookmark : farBookmark}
                        className="clinics-bookmark-icon text-sm transition-all duration-200 group-hover/bookmark:scale-125"
                      />
                    </BtnTaratic>
                  </button>
                ))}

                {conversationsLoading && conversations.length > 0 && (
                  <div data-ui="clinics-medial-history-chat-page-region-025" className="text-center py-2 text-xs text-white/40 animate-pulse">{t('clinics.chat.loadingMore')}</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div data-ui="clinics-medial-history-chat-page-region-026" className="flex-1 rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md flex flex-col overflow-hidden relative z-0">

          {/* Thinking Background Effect */}
          {isStreaming && <ThinkingState />}

          {/* Active Chat Header */}
          {activeConversationId && (
            <div data-ui="clinics-medial-history-chat-page-region-027" className="p-4 border-b border-white/10 flex justify-between items-center flex-shrink-0 z-10 relative">
              <div data-ui="clinics-medial-history-chat-page-region-028" className="flex items-center gap-2">
                <FontAwesomeIcon icon={faRobot} className="clinics-chat-bot-icon text-blue-400" />
                <span className="text-sm font-semibold text-white">
                  {conversations.find(c => c.id === activeConversationId)?.title || `${t('clinics.chat.conversationLabel')} #${activeConversationId}`}
                </span>
              </div>
              <BtnTaratic
                data-ui="clinics-save-filter-button"
                data-pinned={conversations.find(c => c.id === activeConversationId)?.is_saved ? "true" : "false"}
                onClick={handleSaveActiveConversation}
                color={conversations.find(c => c.id === activeConversationId)?.is_saved ? "blue" : "ghost-dark"}
                className="clinics-pinned-action-button w-7 h-7 rounded-lg flex items-center justify-center p-0 group"
                title={conversations.find(c => c.id === activeConversationId)?.is_saved ? t('clinics.chat.saved') : t('clinics.chat.saveConversation')}
              >
                <FontAwesomeIcon
                  icon={conversations.find(c => c.id === activeConversationId)?.is_saved ? fasBookmark : farBookmark}
                  className="clinics-bookmark-icon text-sm transition-all duration-200 group-hover:scale-125"
                />
              </BtnTaratic>
            </div>
          )}

          {/* Welcome overlay if empty and not loading */}
          {messages.length === 0 && !isStreaming && !messagesLoading && (
            <div data-ui="clinics-medial-history-chat-page-region-029" className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center pointer-events-none">
              <div data-ui="clinics-medial-history-chat-page-region-030" className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center text-blue-400 mb-4 border border-blue-500/20">
                <FontAwesomeIcon icon={faRobot} size="2xl" className="clinics-chat-bot-icon" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('clinics.chat.assistantTitle')}</h2>
              <p className="text-white/60 max-w-md mx-auto text-sm">
                {t('clinics.chat.assistantDescription')}
              </p>
              <div data-ui="clinics-medial-history-chat-page-region-031" className="mt-6 flex flex-wrap justify-center gap-2">
                <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-white/80">{t('clinics.chat.suggestion1')}</span>
                <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-white/80">{t('clinics.chat.suggestion2')}</span>
                <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-white/80">{t('clinics.chat.suggestion3')}</span>
              </div>
            </div>
          )}

          {/* Messages List */}
          <div data-ui="clinics-medial-history-chat-page-region-032" className="flex-1 overflow-y-auto gemini-scrollbar p-4 space-y-6 relative z-10">
            {messagesLoading && messages.length === 0 ? (
              // Messages Skeleton
              <div data-ui="clinics-medial-history-chat-page-region-033" className="space-y-6 p-4">
                <div data-ui="clinics-medial-history-chat-page-region-034" className="flex gap-3 items-start animate-pulse">
                  <div data-ui="clinics-medial-history-chat-page-region-035" className="w-8 h-8 rounded-full bg-white/10"></div>
                  <div data-ui="clinics-medial-history-chat-page-region-036" className="space-y-2 max-w-[70%] flex-1">
                    <div data-ui="clinics-medial-history-chat-page-region-037" className="h-4 bg-white/20 rounded w-1/3"></div>
                    <div data-ui="clinics-medial-history-chat-page-region-038" className="h-3 bg-white/10 rounded w-full"></div>
                    <div data-ui="clinics-medial-history-chat-page-region-039" className="h-3 bg-white/10 rounded w-5/6"></div>
                  </div>
                </div>
                <div data-ui="clinics-medial-history-chat-page-region-040" className="flex gap-3 items-start justify-end animate-pulse">
                  <div data-ui="clinics-medial-history-chat-page-region-041" className="space-y-2 max-w-[70%] flex-1 text-right">
                    <div data-ui="clinics-medial-history-chat-page-region-042" className="h-4 bg-white/20 rounded w-1/4 ml-auto"></div>
                    <div data-ui="clinics-medial-history-chat-page-region-043" className="h-3 bg-white/10 rounded w-full ml-auto"></div>
                  </div>
                  <div data-ui="clinics-medial-history-chat-page-region-044" className="w-8 h-8 rounded-full bg-white/10"></div>
                </div>
                <div data-ui="clinics-medial-history-chat-page-region-045" className="flex gap-3 items-start animate-pulse">
                  <div data-ui="clinics-medial-history-chat-page-region-046" className="w-8 h-8 rounded-full bg-white/10"></div>
                  <div data-ui="clinics-medial-history-chat-page-region-047" className="space-y-2 max-w-[70%] flex-1">
                    <div data-ui="clinics-medial-history-chat-page-region-048" className="h-4 bg-white/20 rounded w-1/2"></div>
                    <div data-ui="clinics-medial-history-chat-page-region-049" className="h-3 bg-white/10 rounded w-4/5"></div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.length < totalMessages && activeConversationId && (
                  <div data-ui="clinics-medial-history-chat-page-region-050" className="text-center py-2">
                    <button
                      onClick={() => loadMessages(activeConversationId, messagesPage + 1, true)}
                      disabled={messagesLoading}
                      className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-blue-400 hover:bg-white/10 transition-colors disabled:opacity-50 font-semibold"
                    >
                      {messagesLoading ? t('app.loading') : t('clinics.chat.loadPrevious')}
                    </button>
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <ChatMessageItem
                    key={msg.id}
                    msg={msg}
                    isStreaming={isStreaming}
                    isLastPlaceholder={idx === messages.length - 1}
                  />
                ))}
              </>
            )}
            <div data-ui="clinics-medial-history-chat-page-region-051" ref={messagesEndRef} />
          </div>

          {/* Input Box */}
          <div data-ui="clinics-medial-history-chat-page-region-052" className="p-4 border-t border-white/10 relative z-10">
            <form onSubmit={handleSendMessage} className="relative flex items-center">
              <input
                className="w-full bg-black/75 border border-white/20 rounded-full pl-5 pr-14 py-3.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-sm"
                placeholder={t('clinics.chat.inputPlaceholder')}
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                disabled={isStreaming}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isStreaming}
                className="absolute right-2 w-10 h-10 p-0 m-0 rounded-full bg-blue-500 text-white flex items-center justify-center disabled:opacity-50 disabled:bg-white/10 transition-transform hover:scale-105 active:scale-95 border-0"
              >
                {isStreaming ? (
                  <div data-ui="clinics-medial-history-chat-page-region-053" className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} />
                )}
              </button>
            </form>
          </div>

        </div>
      </div>

      {isNotesModalOpen && patient && (
        <ClinicsPatientNotesModal
          patient={patient}
          onClose={() => setIsNotesModalOpen(false)}
          onSaveSuccess={() => {
            setIsNotesModalOpen(false);
            dispatch(fetchPatientById(Number(id)));
          }}
        />
      )}

      {isSummaryModalOpen && patient && (
        <ClinicsPatientSummaryModal
          patient={patient}
          onClose={() => setIsSummaryModalOpen(false)}
        />
      )}

      {isSaveModalOpen && (
        <div data-ui="clinics-medial-history-chat-page-region-054" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-filter backdrop-blur-md">
          <div data-ui="clinics-medial-history-chat-page-region-055" className="bg-black/80 border border-white/10 p-6 rounded-2xl w-full max-w-md shadow-2xl relative">
            <h3 className="text-lg font-bold text-white mb-4">{t('clinics.chat.saveConversationTitle')}</h3>
            <form onSubmit={submitSaveConversation}>
              <div data-ui="clinics-medial-history-chat-page-region-056" className="space-y-4">
                <div data-ui="clinics-medial-history-chat-page-region-057">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5">{t('clinics.chat.conversationTitleLabel')}</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={t('clinics.chat.conversationTitlePlaceholder')}
                    value={saveTitleInput}
                    onChange={e => setSaveTitleInput(e.target.value)}
                  />
                </div>
              </div>
              <div data-ui="clinics-medial-history-chat-page-region-058" className="flex justify-end gap-3 mt-6">
                <BtnTaratic
                  type="button"
                  onClick={() => setIsSaveModalOpen(false)}
                  color="ghost-dark"
                  size="md"
                >
                  {t('common.cancel')}
                </BtnTaratic>
                <BtnTaratic
                  type="submit"
                  color="green"
                  size="md"
                  className="!shadow-none hover:!shadow-none active:!shadow-none"
                >
                  {t('common.save')}
                </BtnTaratic>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicsMedialHistoryChatPage;
